import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { clearStoredGlobalState } from "../utils/helper";
import Constants from "../Constants";
import { toaster } from "../utils/toast";
import AuthService from "../services/AuthService";
import SparkleIcon from "../assets/icons/Vector.svg?react";
import EditIconBlue from "../assets/icons/EditIconBlue.svg?react";
import useAxios from "./useAxios";
import useDemandCreditCheck from "./useDemandCreditCheck";
import useEasyModeFeatureFlag from "./useEasyModeFeatureFlag";
import useSplitDropdown from "./useSplitDropdown";
import useUserSetting from "./useUserSetting";

// the route segment doubles as the mode key, so there is only one thing to keep in sync
const INTAKE_MODE = {
    EASY: "easy",
    CLASSIC: "edit"
};

const menuIconClasses = "h-action-icon w-action-icon flex-none";

const useAddCaseButton = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { getData, isLoading } = useAxios();
    const { isOpen, toggle } = useSplitDropdown();
    const { confirmDemandCredit } = useDemandCreditCheck();
    const isEasyModeEnabled = useEasyModeFeatureFlag();
    const [prefersClassicIntake, setPrefersClassicIntake] = useUserSetting({ key: "prefersClassicIntake", defaultValue: false });

    // Easy Mode is Diamond-tier only; everyone else only ever creates a classic case
    const defaultIntakeMode = isEasyModeEnabled && !prefersClassicIntake ? INTAKE_MODE.EASY : INTAKE_MODE.CLASSIC;

    const createCaseAndNavigate = async ({ intakeMode }) => {
        try {
            clearStoredGlobalState(dispatch);
            const { homeState } = AuthService.GetLoggedInUserData() || {};
            const addCaseUrl = homeState
                ? `${Constants.ApiUrl.case.addCase}?state=${encodeURIComponent(homeState)}`
                : Constants.ApiUrl.case.addCase;
            const response = await getData(addCaseUrl);
            if (response?.success) {
                navigate(`/case/${intakeMode}/${response.caseId}`, { replace: true });
            }
            else {
                toaster({ message: response.message, success: false });
            }

        } catch (error) {
            console.error("Case creation failed:", error);
        }
    };

    // no plaintiff count exists yet at creation time, so this only asks for the one credit the case will cost at minimum
    const addCase = async ({ intakeMode }) => {
        if (!(await confirmDemandCredit())) return;

        await createCaseAndNavigate({ intakeMode });
    };

    // whichever mode is picked from the menu becomes what the button itself does next time
    const chooseIntakeMode = async ({ intakeMode }) => {
        setPrefersClassicIntake(intakeMode === INTAKE_MODE.CLASSIC);
        await addCase({ intakeMode });
    };

    const menuItems = [
        // the Easy Mode option is only offered to Diamond-tier firms
        ...(isEasyModeEnabled ? [{
            label: "Easy Mode",
            icon: <SparkleIcon className={menuIconClasses} />,
            action: () => chooseIntakeMode({ intakeMode: INTAKE_MODE.EASY }),
        }] : []),
        {
            label: "Classic Intake",
            icon: <EditIconBlue className={menuIconClasses} />,
            action: () => chooseIntakeMode({ intakeMode: INTAKE_MODE.CLASSIC }),
        },
    ];

    return {
        isLoading,
        isOpen,
        toggle,
        menuItems,
        addCaseWithDefaultMode: () => addCase({ intakeMode: defaultIntakeMode }),
    };
};

export default useAddCaseButton;
